import dayjs from 'dayjs';
import _ from 'lodash';

import {
  AiProcessAwaitingStatuses,
  AiProcessExecutionLiveStatuses,
  AiProcessExecutionMode,
  AiProcessExecutionStatus,
  AiProcessStepExecutionStatus,
} from 'constants/aiProcessRuns';
import { getConditionalArray } from 'utils/data';

import {
  AVG_DURATION_WINDOW_DAYS,
  DateGroupKey,
  FAILURE_ONLY_REASON_LABEL_KEYS,
  ManualTriggerSource,
  RATE_SHEET_PERSON_TRIGGER_PREFIX,
  RATE_SHEET_UPLOAD_TRIGGER_PREFIX,
  NEEDS_ATTENTION_WINDOW_DAYS,
  ProcessChipPalette,
  STEP_REASON_LABEL,
} from './AiActivity.constants';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const SECONDS_PAD_LENGTH = 2;
const HASH_BASE = 31;
const HASH_MOD = 1_000_000_007;
const OUTCOME_SEPARATOR = ' · ';
const PARALLEL_OUTCOME_STATUSES = [
  AiProcessExecutionStatus.FAILED,
  AiProcessExecutionStatus.AWAITING_CONFIRMATION,
  AiProcessExecutionStatus.RUNNING,
  AiProcessExecutionStatus.PENDING,
];

export const isValidUuid = value => _.isString(value) && UUID_PATTERN.test(value);

// Canonicalize an execution id to the lowercase form the API serializes and the
// run store therefore keys by (runsById/stepsByRunId). The in-app selection flow
// already feeds lowercase ids straight from the list response, so a route-entered
// or copied deep-link that carries an upper/mixed-case GUID must be normalized the
// same way — otherwise the case-sensitive selector lookup misses the fetched run
// and the drawer stays on "Loading execution…" despite the 200 (DEV-4504).
export const normalizeExecutionId = value => (isValidUuid(value) ? value.toLowerCase() : null);

// Resolve a display name we are willing to show a human. Returns null unless we
// have a real, non-empty name that is NOT itself a raw GUID. The UUID guard is
// belt-and-suspenders for DEV-3030: even if the backend regresses and stuffs a
// user id into triggeredByUserName, we must never leak it into the UI.
const resolveTriggerName = run => {
  const name = run?.triggeredByUserName;

  if (_.isNil(name) || name === '' || isValidUuid(name)) {
    return null;
  }

  return name;
};

const padTwo = value => value.toString().padStart(SECONDS_PAD_LENGTH, '0');

// Paused runs keep counting until someone answers, so durations reach days.
const formatSeconds = totalSeconds => {
  if (totalSeconds < SECONDS_PER_MINUTE) {
    return `${totalSeconds}s`;
  }

  if (totalSeconds < SECONDS_PER_HOUR) {
    return `${Math.floor(totalSeconds / SECONDS_PER_MINUTE)}m ${padTwo(totalSeconds % SECONDS_PER_MINUTE)}s`;
  }

  if (totalSeconds < SECONDS_PER_DAY) {
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

    return `${Math.floor(totalSeconds / SECONDS_PER_HOUR)}h ${padTwo(minutes)}m`;
  }

  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);

  return `${Math.floor(totalSeconds / SECONDS_PER_DAY)}d ${hours}h`;
};

const formatDurationFromDates = (startedAt, completedAt) => {
  if (!startedAt) {
    return '—';
  }

  const startMs = new Date(startedAt).getTime();
  const endMs = completedAt ? new Date(completedAt).getTime() : Date.now();
  const totalSeconds = Math.max(0, Math.round((endMs - startMs) / MILLISECONDS_PER_SECOND));

  return formatSeconds(totalSeconds);
};

export const getDurationSeconds = run => {
  if (!run?.startedAt) {
    return null;
  }

  const startMs = new Date(run.startedAt).getTime();
  const endMs = run?.completedAt ? new Date(run.completedAt).getTime() : Date.now();

  return Math.max(0, Math.round((endMs - startMs) / MILLISECONDS_PER_SECOND));
};

export const formatDurationSeconds = totalSeconds => {
  if (!_.isFinite(totalSeconds)) {
    return '—';
  }

  return formatSeconds(totalSeconds);
};

export const formatRelativeTime = isoDate => {
  if (_.isNil(isoDate) || isoDate === '') {
    return '—';
  }

  const parsed = dayjs(isoDate);

  if (!parsed.isValid()) {
    return '—';
  }

  return parsed.fromNow();
};

export const formatExactTime = isoDate => {
  if (_.isNil(isoDate) || isoDate === '') {
    return '—';
  }

  const parsed = dayjs(isoDate);

  if (!parsed.isValid()) {
    return '—';
  }

  return parsed.format('MMM D, YYYY h:mm A');
};

export const formatTimeOnly = isoDate => {
  if (_.isNil(isoDate) || isoDate === '') {
    return '—';
  }

  const parsed = dayjs(isoDate);

  if (!parsed.isValid()) {
    return '—';
  }

  return parsed.format('h:mm A');
};

export const formatClockTime = isoDate => {
  if (_.isNil(isoDate) || isoDate === '') {
    return '—';
  }

  const parsed = dayjs(isoDate);

  if (!parsed.isValid()) {
    return '—';
  }

  return parsed.format('h:mm:ss A');
};

export const resolveProcessName = (run, processesById) =>
  processesById[run?.aiProcessId]?.name ?? 'AI process';

// Name-only accessor for surfaces that render an avatar/initials rather than the
// full trigger sentence. Reuses the same DEV-3030 UUID guard as formatTriggerInfo
// so a GUID can never reach an initials badge. Returns null for automatic/system
// runs (no person), which the avatar renders as a neutral system glyph.
export const resolveTriggerDisplayName = run => resolveTriggerName(run);

export const formatTriggerInfo = run => {
  // Never surface triggeredByUserId — a raw GUID must not reach the drawer
  // header (DEV-3030). Show the resolved name; for a manual run with no
  // resolvable name fall back to "Unknown" rather than dropping attribution
  // entirely. Automatic runs carry no person, so they stay source-only.
  const name = resolveTriggerName(run);
  const person = name ?? (isManualTrigger(run) ? 'Unknown' : null);
  const parts = [run?.triggerSource, person].filter(value => !_.isNil(value) && value !== '');

  if (_.isEmpty(parts)) {
    return '—';
  }

  return parts.join(' • ');
};

export const formatDuration = run => formatDurationFromDates(run?.startedAt, run?.completedAt);

const hashString = value => {
  if (_.isNil(value) || value === '') {
    return 0;
  }

  return Array.from(String(value)).reduce(
    (hash, character) => (hash * HASH_BASE + character.charCodeAt(0)) % HASH_MOD,
    0,
  );
};

export const assignProcessColor = processId => {
  const index = hashString(processId) % ProcessChipPalette.length;

  return ProcessChipPalette[index];
};

const isManualTrigger = run => {
  const source = run?.triggerSource;

  if (_.isNil(source) || source === '') {
    return false;
  }

  return source.toLowerCase() === ManualTriggerSource.toLowerCase();
};

const isPersonStartedReading = run =>
  String(run?.triggerSource ?? '')
    .toLowerCase()
    .startsWith(RATE_SHEET_PERSON_TRIGGER_PREFIX);

const humanizeAutoTriggerSource = source => {
  const value = String(source).toLowerCase();

  if (value.startsWith(RATE_SHEET_UPLOAD_TRIGGER_PREFIX)) {
    return 'ran after a rate sheet was uploaded';
  }

  if (value.includes('aus')) {
    return 'ran after AUS completed';
  }

  if (value.includes('webhook')) {
    return 'ran after a system event';
  }

  if (value.includes('upload') || value.includes('document')) {
    return 'ran after documents were uploaded';
  }

  if (value.includes('borrower')) {
    return 'ran after borrower info was updated';
  }

  if (value.includes('asset')) {
    return 'ran after an asset was added';
  }

  if (value.includes('income')) {
    return 'ran after income was updated';
  }

  if (value.includes('loanaction')) {
    return 'ran from a loan action';
  }

  if (value.includes('subcompanyaction')) {
    return 'ran from a sub-company action';
  }

  if (value.includes('tpo-cert')) {
    return 'ran for a broker certification';
  }

  return null;
};

export const humanizeTrigger = run => {
  if (!run) {
    return 'started automatically';
  }

  if (isPersonStartedReading(run)) {
    return 'started by a person';
  }

  if (isManualTrigger(run)) {
    // Skip triggeredByUserId entirely — a raw GUID must never render (DEV-3030).
    const who = resolveTriggerName(run) ?? 'a teammate';

    return `started by ${who}`;
  }

  const source = run.triggerSource;
  const autoReason = _.isNil(source) || source === '' ? null : humanizeAutoTriggerSource(source);

  if (!_.isNil(autoReason)) {
    return `automatic — ${autoReason}`;
  }

  // An unrecognised source (API, script, integration) started by a named person
  // is attributed to that person rather than claimed by "the system".
  const who = resolveTriggerName(run);

  return _.isNil(who) ? 'automatic — started by the system' : `started by ${who}`;
};

const RAW_ERROR_PATTERNS = [
  {
    regex: /plaid/i,
    message: "Plaid didn't respond. This usually means Plaid had a brief outage — please retry.",
    isTransient: true,
  },
  {
    regex: /timeout|timed out|deadline/i,
    message:
      'A service took too long to respond. This is usually temporary — please retry in a few minutes.',
    isTransient: true,
  },
  {
    regex: /5\d{2}|service unavailable|bad gateway|connection (reset|refused)/i,
    message: 'A service we depend on had a brief hiccup. This is usually temporary — please retry.',
    isTransient: true,
  },
  {
    regex: /guideline|citation/i,
    message:
      'No clear guideline was found to cite. The AI looked for a relevant rule and could not find one it was confident in.',
    isTransient: false,
  },
  {
    regex: /unauthorized|forbidden|401|403/i,
    message:
      'The AI was not allowed to access something it needed. Please contact engineering — this looks like a permissions issue.',
    isTransient: false,
  },
  {
    regex: /rate limit|throttl/i,
    message:
      'A service slowed us down because of how often we asked it. This is usually temporary — please retry shortly.',
    isTransient: true,
  },
];

// Machine error codes the backend resolves (AiUsageErrorReasonResolver) and now
// surfaces on a failed step. Keyed lower-case so casing drift never silently
// drops us to the generic copy. isTransient mirrors "usually fixes itself on retry".
const ERROR_REASON_COPY = {
  llmtimeout: {
    message:
      'The AI model took too long to respond and the step timed out. This is usually temporary — please retry.',
    isTransient: true,
  },
  llmratelimited: {
    message:
      'We were briefly rate-limited by the AI model. This is temporary — please retry in a moment.',
    isTransient: true,
  },
  llmpaymentrequired: {
    message: 'The AI model rejected the request for billing reasons. Please contact engineering.',
    isTransient: false,
  },
  llmservererror: {
    message: 'The AI model had a server-side error. This is usually temporary — please retry.',
    isTransient: true,
  },
  toolfailure: {
    message:
      'A tool the AI called failed while running. The technical details are available for engineering.',
    isTransient: false,
  },
  cancelled: {
    message: 'The step was cancelled before it could finish.',
    isTransient: false,
  },
  iterationcap: {
    message:
      'The AI hit its step limit before reaching a conclusion. This usually means the task needs to be narrowed.',
    isTransient: false,
  },
  scopeunresolved: {
    message:
      'The AI could not resolve which tools it was allowed to use. Please contact engineering — this looks like a configuration issue.',
    isTransient: false,
  },
  stoppedafterfailure: {
    message: 'The run stopped after another step failed, so this step never started.',
    isTransient: false,
  },
  dependencyoutputmissing: {
    message:
      "A step this one reads from produced no output, so it could not run. Check that step's output, then retry.",
    isTransient: false,
  },
  steplost: {
    message:
      'This step stopped responding and was marked lost. This is usually temporary — please retry.',
    isTransient: true,
  },
};

const resolveReasonCopy = errorReason => {
  if (_.isNil(errorReason) || errorReason === '') {
    return null;
  }

  return ERROR_REASON_COPY[String(errorReason).toLowerCase()] ?? null;
};

// Turn whatever the backend captured into a sentence a human can act on. Prefer
// the structured error reason (stable machine code) over regex-matching the raw
// message; fall back to message patterns, then to a generic — but honest — line.
export const humanizeError = (errorMessage, errorReason) => {
  const reasonCopy = resolveReasonCopy(errorReason);

  if (reasonCopy) {
    return reasonCopy.message;
  }

  if (_.isNil(errorMessage) || errorMessage === '') {
    return 'The AI stopped on this step before it could report why. The raw activity below is the best record of what happened.';
  }

  const match = RAW_ERROR_PATTERNS.find(pattern => pattern.regex.test(errorMessage));

  if (match) {
    return match.message;
  }

  return 'Something went wrong while the AI was working. The technical details are available for engineering.';
};

export const isTransientFailure = (errorMessage, errorReason) => {
  const reasonCopy = resolveReasonCopy(errorReason);

  if (reasonCopy) {
    return reasonCopy.isTransient;
  }

  if (_.isNil(errorMessage) || errorMessage === '') {
    return false;
  }

  return RAW_ERROR_PATTERNS.some(
    pattern => pattern.isTransient && pattern.regex.test(errorMessage),
  );
};

// A step is finished once it can no longer move the run forward: Completed,
// Skipped or Failed. Progress is counted by status, never by position — in a
// Parallel run later steps finish before earlier ones, so "the current step
// number" or "steps before the first unfinished one" undercounts it.
const FINISHED_STEP_STATUSES = new Set([
  AiProcessStepExecutionStatus.COMPLETED,
  AiProcessStepExecutionStatus.SKIPPED,
  AiProcessStepExecutionStatus.FAILED,
]);

const countStatuses = (stepStatuses, predicate) => _.filter(stepStatuses, predicate).length;

// The run model exposes step counts under DEV-3164 keys (`*StepsCount`) with a
// legacy fallback (`*Steps`). Several surfaces (run row, live card, outcome copy,
// the detail progress meter) need the same resolution, so centralise it here
// rather than repeat the `?? ?? 0` ladder.
//
// The REST counts are a snapshot from when the run was loaded; step events keep
// the store's step list current. So when the run's step statuses are known they
// win: a full list is authoritative (it can also go down, e.g. retry-from-here
// resets steps), while a partial live stream never regresses below the snapshot.
//
// `finished` (Completed + Skipped + Failed) is the progress numerator;
// `completed` (Completed only) backs the "N completed" outcome copy. `ratio` is
// finished / total, clamped to [0, 1] so a transiently inconsistent stream never
// overflows a progress track.
export const getRunStepCounts = (run, stepStatuses = []) => {
  const restTotal = run?.totalStepsCount ?? run?.totalSteps ?? 0;
  const restCompleted = run?.completedStepsCount ?? run?.completedSteps ?? 0;
  const restFinished = run?.finishedStepsCount ?? restCompleted;
  const liveCompleted = countStatuses(
    stepStatuses,
    status => status === AiProcessStepExecutionStatus.COMPLETED,
  );
  const liveFinished = countStatuses(stepStatuses, status => FINISHED_STEP_STATUSES.has(status));
  const hasFullStepList = !_.isEmpty(stepStatuses) && stepStatuses.length >= restTotal;
  const total = Math.max(restTotal, _.size(stepStatuses));
  const completed = hasFullStepList ? liveCompleted : Math.max(liveCompleted, restCompleted);
  const finished = hasFullStepList ? liveFinished : Math.max(liveFinished, restFinished);
  const ratio = total > 0 ? Math.min(1, Math.max(0, finished / total)) : 0;

  return { total, completed, finished, ratio };
};

const isParallelRun = run => run?.executionMode === AiProcessExecutionMode.PARALLEL;

const joinNames = names =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${_.last(names)}` : (names[0] ?? '');

const countStepsByStatus = (steps, status) =>
  _.filter(steps, step => step?.status === status).length;

const STEP_FAILED_EVENT_TYPE = 'step_failed';

const getFailedStepNamesFromEvents = (events = []) =>
  _.uniq(
    _.filter(
      events,
      event => event?.type === STEP_FAILED_EVENT_TYPE && !_.isEmpty(event?.message),
    ).map(event => event.message),
  );

const humanizeParallelOutcome = (run, steps) => {
  const { total, completed } = getRunStepCounts(run, _.map(steps, 'status'));

  if (total === 0 || !_.includes(PARALLEL_OUTCOME_STATUSES, run.status)) {
    return null;
  }

  const runningCount = _.isEmpty(steps)
    ? (run.runningStepCount ?? 0)
    : countStepsByStatus(steps, AiProcessStepExecutionStatus.RUNNING);
  const failedNames = _.isEmpty(steps)
    ? getFailedStepNamesFromEvents(run.recentEvents)
    : _.filter(steps, step => step?.status === AiProcessStepExecutionStatus.FAILED).map(
        step => step.name,
      );
  const skippedCount = countStepsByStatus(steps, AiProcessStepExecutionStatus.SKIPPED);
  const completeLabel = `${completed} of ${total} complete`;

  if (run.status === AiProcessExecutionStatus.FAILED) {
    return [
      completeLabel,
      failedNames.length > 0 ? `${joinNames(failedNames)} failed` : 'a step failed',
      ...getConditionalArray(skippedCount > 0, () => [`${skippedCount} skipped`]),
    ].join(OUTCOME_SEPARATOR);
  }

  if (run.status === AiProcessExecutionStatus.AWAITING_CONFIRMATION) {
    return [
      completeLabel,
      'waiting for confirmation',
      ...getConditionalArray(runningCount > 0, () => [`${runningCount} still running`]),
    ].join(OUTCOME_SEPARATOR);
  }

  return [
    `working — ${completeLabel}`,
    ...getConditionalArray(runningCount > 1, () => [`${runningCount} running`]),
  ].join(OUTCOME_SEPARATOR);
};

export const humanizeOutcome = (run, steps = []) => {
  if (!run) {
    return '';
  }

  const parallelOutcome = isParallelRun(run) ? humanizeParallelOutcome(run, steps) : null;

  if (!_.isNil(parallelOutcome)) {
    return parallelOutcome;
  }

  const { total, completed, finished } = getRunStepCounts(run, _.map(steps, 'status'));

  if (run.status === AiProcessExecutionStatus.COMPLETED) {
    if (total > 0) {
      return `${completed} of ${total} steps completed`;
    }

    return 'completed';
  }

  if (run.status === AiProcessExecutionStatus.FAILED) {
    if (total > 0) {
      return `stopped at step ${completed + 1} of ${total}`;
    }

    return 'stopped because of a problem';
  }

  if (run.status === AiProcessExecutionStatus.CANCELLED) {
    return 'cancelled';
  }

  if (run.status === AiProcessExecutionStatus.AWAITING_CONFIRMATION) {
    if (total > 0) {
      return `waiting for confirmation at step ${completed + 1} of ${total}`;
    }

    return 'waiting for confirmation';
  }

  if (total > 0) {
    return `working — ${finished} of ${total} steps so far`;
  }

  return 'working';
};

export const groupRunsByDate = runs => {
  const now = dayjs();
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.subtract(1, 'day');
  const startOfThisWeek = startOfToday.subtract(now.day(), 'day');

  const buckets = {
    [DateGroupKey.TODAY]: [],
    [DateGroupKey.YESTERDAY]: [],
    [DateGroupKey.THIS_WEEK]: [],
    [DateGroupKey.EARLIER]: [],
  };

  runs.forEach(run => {
    const started = dayjs(run.startedAt);

    if (!started.isValid()) {
      buckets[DateGroupKey.EARLIER].push(run);

      return;
    }

    if (started.isAfter(startOfToday)) {
      buckets[DateGroupKey.TODAY].push(run);

      return;
    }

    if (started.isAfter(startOfYesterday)) {
      buckets[DateGroupKey.YESTERDAY].push(run);

      return;
    }

    if (started.isAfter(startOfThisWeek)) {
      buckets[DateGroupKey.THIS_WEEK].push(run);

      return;
    }

    buckets[DateGroupKey.EARLIER].push(run);
  });

  // RunHistoryCard renders this with `.map`, reading `group.key`/`group.runs`
  // (mirroring groupRunsByProcess). Return an ordered array of non-empty
  // groups rather than the raw bucket object — an object has no `.map` and
  // crashed the tab to the error boundary as soon as a loan had any runs.
  const orderedKeys = [
    DateGroupKey.TODAY,
    DateGroupKey.YESTERDAY,
    DateGroupKey.THIS_WEEK,
    DateGroupKey.EARLIER,
  ];

  return orderedKeys
    .filter(key => buckets[key].length > 0)
    .map(key => ({ key, runs: buckets[key] }));
};

export const groupRunsByProcess = (runs, processesById) => {
  const buckets = new Map();

  runs.forEach(run => {
    const processId = run.aiProcessId ?? 'unknown';
    const name = resolveProcessName(run, processesById);
    const existing = buckets.get(processId) ?? { name, processId, runs: [] };

    existing.runs.push(run);
    buckets.set(processId, existing);
  });

  return Array.from(buckets.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const computeProcessGroupStats = groupRuns => {
  const total = groupRuns.length;
  const done = groupRuns.filter(run => run.status === AiProcessExecutionStatus.COMPLETED).length;
  const problems = groupRuns.filter(run => run.status === AiProcessExecutionStatus.FAILED).length;
  const durations = groupRuns
    .map(getDurationSeconds)
    .filter(value => _.isFinite(value) && value !== null);
  const median = durations.length ? computeMedian(durations) : null;
  const lastRun = _.maxBy(groupRuns, run => new Date(run.startedAt).getTime());

  return {
    total,
    done,
    problems,
    medianSeconds: median,
    lastRunAt: lastRun?.startedAt ?? null,
    problemRate: total === 0 ? 0 : problems / total,
  };
};

const computeMedian = values => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
  }

  return sorted[mid];
};

export const computeOverviewMetrics = runs => {
  const now = dayjs();
  const cutoffNeedsAttention = now.subtract(NEEDS_ATTENTION_WINDOW_DAYS, 'day');
  const cutoffAvg = now.subtract(AVG_DURATION_WINDOW_DAYS, 'day');

  const liveRuns = runs.filter(
    run =>
      run.status === AiProcessExecutionStatus.RUNNING ||
      run.status === AiProcessExecutionStatus.PENDING,
  );

  const recentFailures = runs.filter(
    run =>
      run.status === AiProcessExecutionStatus.FAILED &&
      dayjs(run.completedAt ?? run.startedAt).isAfter(cutoffNeedsAttention),
  );

  const actionableFailureCount = recentFailures.filter(
    run => !isTransientFailure(run.errorMessage),
  ).length;
  // A run waiting on a confirmation or an answer cannot move until someone acts on it.
  const waitingOnUserCount = runs.filter(
    run =>
      run.status === AiProcessExecutionStatus.AWAITING_CONFIRMATION ||
      run.status === AiProcessExecutionStatus.AWAITING_USER_INPUT,
  ).length;

  const completedRecent = runs.filter(
    run =>
      run.status === AiProcessExecutionStatus.COMPLETED &&
      dayjs(run.completedAt ?? run.startedAt).isAfter(cutoffAvg),
  );
  const durations = completedRecent
    .map(getDurationSeconds)
    .filter(value => _.isFinite(value) && value !== null);
  const avgSeconds = durations.length
    ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length)
    : null;

  const lastActivity = _.maxBy(runs, run =>
    new Date(run.completedAt ?? run.startedAt ?? 0).getTime(),
  );

  return {
    liveRunsCount: liveRuns.length,
    lastActivityAt: lastActivity?.completedAt ?? lastActivity?.startedAt ?? null,
    lastActivityProcessId: lastActivity?.aiProcessId ?? null,
    needsAttentionCount: actionableFailureCount + waitingOnUserCount,
    recurringFailureCount: recentFailures.length - actionableFailureCount,
    avgSeconds,
  };
};

const humanizeEventType = eventType => {
  if (_.isNil(eventType) || eventType === '') {
    return 'Updated';
  }

  const value = String(eventType).toLowerCase();

  if (value.includes('start')) {
    return 'Started';
  }

  if (value.includes('complete') || value.includes('finish')) {
    return 'Finished';
  }

  if (value.includes('fail') || value.includes('error')) {
    return 'Stopped because of a problem';
  }

  if (value.includes('cancel')) {
    return 'Cancelled';
  }

  if (value.includes('tool')) {
    return 'Used a tool';
  }

  return 'Updated';
};

const shortEventVerb = eventType => {
  const value = String(eventType ?? '').toLowerCase();

  if (value.includes('start')) {
    return 'started';
  }

  if (value.includes('complete') || value.includes('finish')) {
    return 'finished';
  }

  if (value.includes('fail') || value.includes('error')) {
    return 'stopped';
  }

  if (value.includes('cancel')) {
    return 'cancelled';
  }

  return null;
};

// Compose the timeline line from the step name (event.message) AND the action,
// so an event reads "Income Verification — stopped" instead of the contextless
// "Stopped because of a problem" the bare event type produced before.
export const formatEventLabel = event => {
  const name = event?.message;
  const verb = shortEventVerb(event?.type);

  if (_.isNil(name) || name === '') {
    return humanizeEventType(event?.type);
  }

  return verb ? `${name} — ${verb}` : name;
};

// A failed step terminates a sequential run server-side, but the run status can lag
// (transition delay / SignalR catch-up), leaving a stale "Running" in the header
// while a step shows Failed. When such a run is still live but a step has already
// failed, report Failed so the header never contradicts the step list. A parallel
// run is different: a failed step does not end it while independent steps keep
// running, so its server-projected status is authoritative.
export const deriveDisplayRunStatus = (execution, steps) => {
  const status = execution?.status ?? null;

  if (!AiProcessExecutionLiveStatuses.has(status) || isParallelRun(execution)) {
    return status;
  }

  const hasFailedStep = _.some(steps, step => step?.status === AiProcessStepExecutionStatus.FAILED);

  return hasFailedStep ? AiProcessExecutionStatus.FAILED : status;
};

const resolveStepWave = (step, index) => step?.wave ?? step?.stepNumber ?? index + 1;

export const groupStepsByWave = steps =>
  _.reduce(
    steps,
    (groups, step, index) => {
      const wave = resolveStepWave(step, index);
      const lastGroup = _.last(groups);

      if (lastGroup?.wave === wave) {
        lastGroup.steps.push(step);

        return groups;
      }

      groups.push({ wave, steps: [step] });

      return groups;
    },
    [],
  );

// Historical runs carry no dependency graph, so they read as a chain: each step
// depends on the one before it in the stepNumber-sorted list.
const getStepDependencyIds = (step, index, steps) =>
  step?.dependsOnStepExecutionIds ?? (index > 0 ? [steps[index - 1].id] : []);

const isBlockingSeed = step =>
  step?.status === AiProcessStepExecutionStatus.FAILED ||
  AiProcessAwaitingStatuses.has(step?.status);

// Pending steps that cannot start because a step they depend on (directly or
// transitively) failed or is paused. Maps the blocked step id to the step that
// holds it up, so the nav rail can say "Waits on <step>".
export const resolveBlockedStepIds = steps => {
  const blockerByStepId = new Map();

  if (!_.isArray(steps)) {
    return blockerByStepId;
  }

  const stepsById = _.keyBy(steps, 'id');
  const seedIds = new Set(steps.filter(isBlockingSeed).map(step => step.id));

  steps.forEach((step, index) => {
    if (step?.status !== AiProcessStepExecutionStatus.PENDING) {
      return;
    }

    const dependencyIds = getStepDependencyIds(step, index, steps);
    const seedDependencyId = dependencyIds.find(id => seedIds.has(id));
    const blockedDependencyId = dependencyIds.find(id => blockerByStepId.has(id));

    if (!_.isNil(seedDependencyId)) {
      blockerByStepId.set(step.id, stepsById[seedDependencyId]);

      return;
    }

    if (!_.isNil(blockedDependencyId)) {
      blockerByStepId.set(step.id, blockerByStepId.get(blockedDependencyId));
    }
  });

  return blockerByStepId;
};

export const getDescendantStepIds = (steps, stepId) => {
  const descendantIds = new Set();

  _.forEach(steps, (step, index) => {
    const dependencyIds = getStepDependencyIds(step, index, steps);

    if (dependencyIds.includes(stepId) || dependencyIds.some(id => descendantIds.has(id))) {
      descendantIds.add(step.id);
    }
  });

  return descendantIds;
};

const getStartedAtMs = step => (step.startedAt ? new Date(step.startedAt).getTime() : Infinity);

export const resolveDefaultStepId = steps => {
  const needsInput = steps.find(step => AiProcessAwaitingStatuses.has(step.status));
  const failed = steps.find(step => step.status === AiProcessStepExecutionStatus.FAILED);
  const running = _.minBy(
    steps.filter(step => step.status === AiProcessStepExecutionStatus.RUNNING),
    getStartedAtMs,
  );

  return (needsInput ?? failed ?? running ?? steps[0]).id;
};

export const getRunLiveCounts = ({ run, steps }) => {
  const hasSteps = !_.isEmpty(steps);
  const runningCount = hasSteps
    ? countStepsByStatus(steps, AiProcessStepExecutionStatus.RUNNING)
    : (run?.runningStepCount ?? 0);
  const isNeedsInput =
    AiProcessAwaitingStatuses.has(run?.status) ||
    _.some(steps, step => AiProcessAwaitingStatuses.has(step?.status));

  return { runningCount, isNeedsInput };
};

export const formatNeedsInputLabel = runningCount =>
  `Needs your input · ${runningCount} still running`;

export const formatRunProgressLabel = ({ completed, total, runningCount, isNeedsInput }) => {
  if (isNeedsInput && runningCount > 0) {
    return formatNeedsInputLabel(runningCount);
  }

  const stepsLabel = `${completed} of ${total} steps`;

  return runningCount > 0 ? `${stepsLabel}${OUTCOME_SEPARATOR}${runningCount} running` : stepsLabel;
};

export const formatLiveRunHeadline = counts => {
  const label = formatRunProgressLabel(counts);

  if (!counts.isNeedsInput) {
    return `Working — ${label}`;
  }

  return counts.runningCount > 0 ? label : `Needs your input — ${label}`;
};

export const getStepReasonLabel = step => {
  const reasonKey = String(step?.errorReason ?? '').toLowerCase();
  const label = STEP_REASON_LABEL[reasonKey] ?? null;

  if (step?.status === AiProcessStepExecutionStatus.SKIPPED) {
    return label;
  }

  if (step?.status === AiProcessStepExecutionStatus.FAILED) {
    return FAILURE_ONLY_REASON_LABEL_KEYS.has(reasonKey) ? label : null;
  }

  return null;
};

export const formatRetryFromHereScope = dependentCount => {
  if (!_.isFinite(dependentCount)) {
    return 'Re-running this step and all later steps may repeat actions already taken. Continue?';
  }

  const dependents =
    dependentCount === 0
      ? ''
      : ` and the ${dependentCount} ${dependentCount === 1 ? 'step' : 'steps'} that depend${dependentCount === 1 ? 's' : ''} on it`;

  return `Re-runs this step${dependents}. This may repeat actions already taken. Continue?`;
};
